/* ================= Groups =================
   A group is a saved set of students with a name and an optional price per head
   (kept in the settings, so it syncs like everything else). Planning a lesson for a
   group creates one lesson (or series) per member, linked by the group's id: billing,
   reminders, recaps and the parent page stay per student. */
import { addDays, fmtDM, money, today } from '../dates.js';
import { t } from '../i18n/index.js';
import { eventsIn, stu } from '../model.js';
import { S, persist, uid } from '../state.js';
import { IC, ask, avatarHtml, closeSheet, esc, render, sheet, toast } from '../ui.js';
import { dropLessonPics } from '../cloud/media.js';

export const GRP_IC='<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3.2"/><circle cx="16.5" cy="9.5" r="2.5"/><path d="M3.5 19c.6-3.2 2.8-4.8 5.5-4.8s4.9 1.6 5.5 4.8M14.5 14.6c.6-.3 1.3-.4 2-.4 2.2 0 3.7 1.4 4.2 4"/></svg>';

export function groups(){
  if(!Array.isArray(S.settings.groups))S.settings.groups=[];
  return S.settings.groups;
}
export const groupById=id=>groups().find(g=>g.id===id)||null;
/* members that still exist and are active */
export const groupStudents=g=>(g.members||[]).map(stu).filter(s=>s&&!s.archived);

function nextLesson(g){
  const from=today(),list=eventsIn(from,addDays(from,60)).filter(e=>e.gid===g.id&&e.status==='planned');
  return list[0]||null;
}

/* ---------- the "Gruppen" card on the Schüler page ---------- */
export function groupsCard(){
  const gs=groups();
  const rows=gs.map(g=>{
    const ss=groupStudents(g),nx=nextLesson(g);
    return `<div class="listrow click" data-grp="${g.id}">
      <div class="avstack">${ss.slice(0,3).map(s=>avatarHtml(s)).join('')||'<span class="grpav">'+GRP_IC+'</span>'}</div>
      <div class="grow"><div class="title">${esc(g.name)}</div>
        <div class="meta">${t('grp.n',{n:ss.length})}${g.rate!=null&&g.rate!==''?' · '+esc(t('grp.perHead',{p:money(+g.rate)})):''}${nx?' · '+esc(t('grp.next',{d:fmtDM(nx.date),time:nx.time})):''}</div></div>
      <button class="iconbtn" data-grpplan="${g.id}" title="${esc(t('grp.plan'))}" aria-label="${esc(t('grp.plan'))}">${IC.cal}</button></div>`;
  }).join('');
  return `<div class="card mt grpcard"><div class="cardhead"><h2>${t('grp.titlePl')}</h2><button class="btn small" data-a="newgroup">${IC.plus} ${t('grp.new')}</button></div>
    ${rows||`<p class="muted emptyline">${t('grp.empty')}</p>`}</div>`;
}
export function bindGroups(m,openLessonEditor){
  m.querySelectorAll('[data-grp]').forEach(el=>el.addEventListener('click',e=>{if(e.target.closest('[data-grpplan]'))return;openGroupEditor(el.dataset.grp,openLessonEditor)}));
  m.querySelectorAll('[data-grpplan]').forEach(el=>el.onclick=e=>{e.stopPropagation();openLessonEditor(null,today(),false,{groupId:el.dataset.grpplan})});
  m.querySelectorAll('[data-a=newgroup]').forEach(el=>el.onclick=()=>openGroupEditor(null,openLessonEditor));
}

/* ---------- create / edit a group ---------- */
export function openGroupEditor(id,openLessonEditor,onSaved){
  const g=id?groupById(id):null;
  const isNew=!g;
  const sel=new Set(g?g.members||[]:[]);
  const all=S.students.filter(s=>!s.archived||sel.has(s.id)).sort((a,b)=>a.first.localeCompare(b.first));
  const b=sheet(`<div class="sheethead"><h2>${isNew?t('grp.new'):t('grp.editT')}</h2><button class="iconbtn" data-a="x" aria-label="${esc(t('c.close'))}">${IC.x}</button></div>
  <div class="sheetbody">
    <div class="field"><label for="gr-name">${t('grp.name')}</label><input class="input" id="gr-name" value="${esc(g?g.name:'')}" placeholder="${esc(t('grp.namePh'))}" autocomplete="off"></div>
    <div class="field"><label for="gr-rate">${t('grp.rate')} <span class="tiny">(${t('c.optional')})</span></label><input class="input" id="gr-rate" type="number" step="0.5" min="0" inputmode="decimal" value="${g&&g.rate!=null?g.rate:''}" placeholder="${esc(t('grp.ratePh'))}"></div>
    <div class="field"><div class="grphead"><label>${t('grp.members')}</label><span class="tiny" id="gr-count"></span></div>
      ${all.length>8?`<input class="input mb8" id="gr-q" placeholder="${esc(t('stu.searchPh'))}" autocomplete="off">`:''}
      <div class="grppick" role="group" aria-label="${esc(t('grp.members'))}">${all.map(s=>`<label class="grpopt" data-n="${esc((s.first+' '+(s.last||'')).toLowerCase())}"><input type="checkbox" value="${s.id}" ${sel.has(s.id)?'checked':''}>${avatarHtml(s)}<span class="grow">${esc(s.first+' '+(s.last||''))}</span><span class="grptick" aria-hidden="true">${IC.check}</span></label>`).join('')}</div>
      ${all.length?'':`<p class="muted">${t('misc.needStudent')}</p>`}
    </div>
    ${isNew?'':`<p class="hint">${t('grp.applyHint')}</p>`}
    <p class="fielderr" id="gr-err" hidden></p>
  </div>
  <div class="sheetfoot">${isNew?'':`<div class="left"><button class="btn danger" data-a="del" aria-label="${esc(t('grp.delete'))}">${IC.trash}</button></div>`}
    <button class="btn" data-a="cancel">${t('c.cancel')}</button>
    <button class="btn primary" data-a="save">${isNew?t('grp.create'):t('c.save')}</button></div>`);
  const q=x=>b.querySelector(x);
  const count=()=>{const n=b.querySelectorAll('.grppick input:checked').length;q('#gr-count').textContent=t('grp.n',{n})};count();
  b.querySelectorAll('.grppick input').forEach(i=>i.onchange=count);
  if(q('#gr-q'))q('#gr-q').oninput=e=>{const v=e.target.value.trim().toLowerCase();b.querySelectorAll('.grpopt').forEach(o=>o.hidden=!!v&&!o.dataset.n.includes(v))};
  q('[data-a=x]').onclick=q('[data-a=cancel]').onclick=()=>closeSheet(b);
  if(!isNew)q('[data-a=del]').onclick=()=>ask(t('grp.deleteAsk',{name:g.name}),()=>{deleteGroup(g);closeSheet(b);render();toast(t('grp.deleted'))},true);
  setTimeout(()=>{if(isNew)q('#gr-name').focus()},250);
  q('[data-a=save]').onclick=()=>{
    const name=q('#gr-name').value.trim(),rv=q('#gr-rate').value.trim();
    const members=[...b.querySelectorAll('.grppick input:checked')].map(i=>i.value);
    const err=q('#gr-err');err.hidden=true;
    if(!name){err.textContent=t('grp.eName');err.hidden=false;q('#gr-name').focus();return}
    if(members.length<2){err.textContent=t('grp.eTwo');err.hidden=false;return}
    const rate=rv===''?null:+rv;
    if(isNew){
      const ng={id:uid(),name,members,rate};groups().push(ng);persist();closeSheet(b);render();
      toast(t('grp.created',{name}));
      if(onSaved)onSaved(ng);
      else if(openLessonEditor)setTimeout(()=>openLessonEditor(null,today(),false,{groupId:ng.id}),280);
      return;
    }
    applyGroupChange(g,{name,members,rate});
    persist();closeSheet(b);render();toast(t('c.saved'));
    if(onSaved)onSaved(g);
  };
}

/* name, price and members carry over to lessons from today on; what already happened stays */
function applyGroupChange(g,next){
  const from=today();
  const added=next.members.filter(id=>!(g.members||[]).includes(id));
  const removed=(g.members||[]).filter(id=>!next.members.includes(id));
  g.name=next.name;g.rate=next.rate;g.members=next.members;
  const recs=S.recs.filter(r=>r.gid===g.id&&!r.ended&&(!r.until||r.until>=from));
  const futureSes=S.sessions.filter(x=>x.gid===g.id&&x.date>=from&&x.status==='planned'&&!x.invoiceId);
  for(const r of S.recs)if(r.gid===g.id){r.gname=g.name;if(!r.until||r.until>=from)r.rate=g.rate}
  for(const x of S.sessions)if(x.gid===g.id){x.gname=g.name;if(x.date>=from&&x.status==='planned'&&!x.invoiceId)x.rate=g.rate}
  /* the series template: one rec per distinct time slot of this group */
  const slots=[];for(const r of recs){if(!slots.some(s=>s.time===r.time&&s.freq===r.freq&&String(s.days)===String(r.days)))slots.push(r)}
  for(const id of added){
    for(const r of slots)S.recs.push({id:uid(),studentId:id,time:r.time,dur:r.dur,meet:r.meet||'',freq:r.freq,days:[...r.days],start:r.start>from?r.start:from,until:r.until||null,gid:g.id,gname:g.name,rate:g.rate});
    const single=[];for(const x of futureSes){if(!x.recId&&!single.some(y=>y.date===x.date&&y.time===x.time))single.push(x)}
    for(const x of single)S.sessions.push({id:uid(),studentId:id,date:x.date,time:x.time,dur:x.dur,status:'planned',meet:x.meet||'',rate:g.rate,notes:'',gid:g.id,gname:g.name});
  }
  for(const id of removed){
    for(const r of S.recs.filter(r=>r.gid===g.id&&r.studentId===id)){
      if(r.start>=from&&!S.sessions.some(x=>x.recId===r.id&&(x.status==='done'||x.invoiceId)))S.recs=S.recs.filter(z=>z!==r);
      else r.until=addDays(from,-1);
    }
    const gone=x=>x.gid===g.id&&x.studentId===id&&x.date>=from&&x.status==='planned'&&!x.invoiceId;
    dropLessonPics(S.sessions.filter(gone));S.sessions=S.sessions.filter(x=>!gone(x));
  }
}
/* deleting a group cancels its lessons from today on; held or invoiced lessons stay */
function deleteGroup(g){
  const from=today();
  for(const r of S.recs.filter(r=>r.gid===g.id)){
    if(r.start>=from&&!S.sessions.some(x=>x.recId===r.id&&(x.status==='done'||x.invoiceId)))S.recs=S.recs.filter(z=>z!==r);
    else r.until=addDays(from,-1);
  }
  const gone=x=>x.gid===g.id&&x.date>=from&&x.status==='planned'&&!x.invoiceId;
  dropLessonPics(S.sessions.filter(gone));S.sessions=S.sessions.filter(x=>!gone(x));
  S.settings.groups=groups().filter(x=>x.id!==g.id);
}
